import React, { useState, useMemo } from 'react';
import ConditionsDropdown from '../ConditionsDropdown';
import { isTaskEffectivelyCompleted } from '../../FlowchartView/utils/dependencyUtils/helpers';
import { getEffectiveConditionsV2, hasAnyConditions } from '../ConditionsDropdown/utils/conditionHelpers';
import { formatWaitDurationWords, getWaitSeconds, getConditionAvailableAtMs } from '../ConditionsDropdown/utils/conditionWait';
import { useWaitGateNow } from '../ConditionsDropdown/utils/useWaitGateNow';
import { findPreferredStatus } from '../../FlowchartView/utils/statusManagement';
import { DEFAULT_OPTIONS } from '../../FlowchartView/TaskNode/DecisionOptionsModal';

const StatusMark = ({ label, color }) => (
  <span
    className="mfv-metadata__status-mark"
    style={{
      '--status-color': color || '#22c55e',
      '--status-bg': `${color || '#22c55e'}20`,
    }}
  >
    {label}
  </span>
);

const OperatorWord = ({ op, className = '' }) => (
  <div className={`mfv-metadata__operator-word ${className}`.trim()}>
    {(op || 'AND').toLowerCase()}
  </div>
);

const ConditionSentence = ({ condition, completedStatus, isUnlocked }) => {
  const task = condition.task;
  const taskName = task?.name || 'Unknown mission';
  const isMet = condition.isMet;
  const isMuted = isUnlocked && !isMet;
  const waitDuration = formatWaitDurationWords(condition.waitSeconds);
  const isDecision = task?.nodeType === 'decision';
  const options = task?.options?.length ? task.options : DEFAULT_OPTIONS;
  const choice = isDecision
    ? options.find((o) => o.id === condition.branch) || options[0]
    : null;

  return (
    <div className={`mfv-metadata__condition ${isMet ? 'mfv-metadata__condition--met' : ''} ${isMuted ? 'mfv-metadata__condition--muted' : ''}`}>
      <p className="mfv-metadata__condition-sentence">
        <span className="mfv-metadata__condition-name">{taskName}</span>
        {isDecision ? ' is answered as ' : ' is marked as '}
        <StatusMark
          label={isDecision ? (choice?.label || choice?.name || 'a choice') : completedStatus.name}
          color={isDecision ? choice?.color : completedStatus.color}
        />
      </p>
      {waitDuration && (
        <p className="mfv-metadata__condition-wait">
            Followed by {waitDuration}
        </p>
      )}
    </div>
  );
};

/**
 * Readable unlock rules in the mission sidebar (and stacked conditions views).
 * Mirrors v2 blocks: inner AND/OR inside a group, outer AND/OR between groups.
 */
const DependenciesSection = ({
  task,
  allTasks = [],
  statuses = [],
  nestedSprints = [],
  onConditionsChange,
  className = '',
}) => {
  const [isConditionsModalOpen, setIsConditionsModalOpen] = useState(false);

  const v2 = useMemo(() => getEffectiveConditionsV2(task), [task]);
  const hasConditions = hasAnyConditions(v2);
  const waitNow = useWaitGateNow(allTasks);
  const preferredCompleted = findPreferredStatus(statuses, 'completed');
  const completedStatus = {
    name: preferredCompleted?.name || 'Done',
    color: preferredCompleted?.color || '#22c55e',
  };

  const { displayBlocks, hasMultipleBlocks, isUnlocked } = useMemo(() => {
    if (!hasConditions || !v2.blocks || v2.blocks.length === 0) {
      return { displayBlocks: [], hasMultipleBlocks: false, isUnlocked: true };
    }

    const blocks = v2.blocks.map((block) => {
      const conditions = (block.conditions || []).map((cond) => {
        const condTask = allTasks.find((t) => t.id === cond.taskId);
        const isDecision = condTask?.nodeType === 'decision';
        let done = false;
        if (condTask) {
          if (isDecision && cond.branch) {
            const chosen = condTask.answer || condTask.selectedOption;
            done = chosen === cond.branch;
          } else {
            done = isTaskEffectivelyCompleted(condTask, nestedSprints, statuses);
          }
        }
        const waitSeconds = getWaitSeconds(cond);
        const availableAt = done ? getConditionAvailableAtMs(cond, condTask) : null;
        const now = waitNow ?? Date.now();
        const remainingMs = availableAt != null && now < availableAt ? availableAt - now : null;
        const isMet = done && remainingMs == null;
        return { ...cond, task: condTask, isMet, waitSeconds, remainingMs };
      });

      const innerOp = block.innerOperator || 'AND';
      const blockSatisfied = innerOp === 'AND'
        ? conditions.every((c) => c.isMet)
        : conditions.some((c) => c.isMet);

      return {
        conditions,
        innerOperator: innerOp,
        outerOperator: block.outerOperator,
        isSatisfied: blockSatisfied,
      };
    });

    let unlocked = blocks[0].isSatisfied;
    for (let i = 1; i < blocks.length; i++) {
      const op = blocks[i].outerOperator || 'AND';
      unlocked = op === 'AND' ? unlocked && blocks[i].isSatisfied : unlocked || blocks[i].isSatisfied;
    }

    return {
      displayBlocks: blocks,
      hasMultipleBlocks: blocks.length > 1,
      isUnlocked: unlocked,
    };
  }, [v2, allTasks, nestedSprints, statuses, hasConditions, waitNow]);

  return (
    <div className={`mfv-metadata__group ${className}`}>
      <div className="mfv-metadata__section-header">
        <h3 className="mfv-metadata__section-title">Dependencies</h3>
        {onConditionsChange && (
          <button
            className="mfv-metadata__edit-btn"
            onClick={() => setIsConditionsModalOpen(true)}
            type="button"
          >
            {hasConditions ? 'Edit' : 'Add'}
          </button>
        )}
      </div>

      {hasConditions ? (
        <>
          <p className="mfv-metadata__conditions-explanation">
            This mission can only be started once:
          </p>

          <div className="mfv-metadata__options">
            {displayBlocks.map((block, blockIndex) => {
              const grouped = hasMultipleBlocks && block.conditions.length > 1;
              return (
                <div key={blockIndex} className="mfv-metadata__option-wrapper">
                  {blockIndex > 0 && (
                    <OperatorWord op={block.outerOperator} className="mfv-metadata__operator-word--outer" />
                  )}

                  <div className={`mfv-metadata__option ${block.isSatisfied ? 'mfv-metadata__option--satisfied' : ''} ${grouped ? 'mfv-metadata__option--grouped' : ''}`}>
                    <div className="mfv-metadata__conditions">
                      {block.conditions.map((condition, condIndex) => (
                        <React.Fragment key={condIndex}>
                          {condIndex > 0 && (
                            <OperatorWord op={block.innerOperator} />
                          )}
                          <ConditionSentence
                            condition={condition}
                            completedStatus={completedStatus}
                            isUnlocked={isUnlocked}
                          />
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <p className={`mfv-metadata__conditions-status ${isUnlocked ? 'mfv-metadata__conditions-status--unlocked' : 'mfv-metadata__conditions-status--locked'}`}>
            {isUnlocked ? 'Unlocked' : 'Locked'}
          </p>
        </>
      ) : (
        <p className="mfv-metadata__empty-state">
          No dependencies
        </p>
      )}

      {onConditionsChange && (
        <ConditionsDropdown
          task={task}
          allTasks={allTasks}
          onConditionsChange={onConditionsChange}
          isOpen={isConditionsModalOpen}
          onClose={() => setIsConditionsModalOpen(false)}
        />
      )}
    </div>
  );
};

export default DependenciesSection;
